import type { FormikProps } from 'formik';
import { useCallback } from 'react';

import './VendorLeadStepAddressTab.scss';

import { SelectField, TextInputField } from 'src/components/base';
import type { IVendorProductLeadAddressTabFormValues } from 'src/components/vendors/steps/Contact/VendorLeadStepAddressTab.validation';
import { VendorLeadStepLayoutNextButton } from 'src/components/vendors/steps/VendorLeadStepLayoutNextButton';
import { useDebounceEffect } from 'src/hooks/useDebounceEffect';
import { CANADA_STATE_OPTIONS, COUNTRY_OPTIONS, Country, USA_STATE_OPTIONS } from 'src/types';

type VendorLeadStepContactTabProps = {
  formikProps: FormikProps<IVendorProductLeadAddressTabFormValues>;

  onChange: (newValues: IVendorProductLeadAddressTabFormValues) => void;
};

export function VendorLeadStepAddressTabContent({
  formikProps: { values, setValues, validateForm },
  onChange,
}: Readonly<VendorLeadStepContactTabProps>) {
  const handleCountryChange = useCallback(
    (newCountry: string) => {
      void setValues(
        (prev) => ({
          ...prev,

          address: {
            country: newCountry,

            region: null,
            city: null,
            street: null,
            postalCode: null,
          },
        }),
        false
      );
    },
    [setValues]
  );

  useDebounceEffect(
    () => {
      void (async () => {
        const errors = await validateForm(values);

        if (Object.keys(errors).length === 0) {
          onChange(values);
        }
      })();
    },
    [values],
    1000
  );

  const country = values.address.country;
  const isRegionDisabled = !country;

  return (
    <div className="vendor-lead-step-address-tab__container">
      <div className="vendor-lead-step-address-tab__container__row">
        <div className="w-100">
          <SelectField
            name="address.country"
            label="Country"
            options={COUNTRY_OPTIONS}
            onChange={handleCountryChange}
          />
        </div>
        <div className="w-100">
          <SelectField
            name="address.region"
            label={country === Country.Canada ? 'Province/Territory' : 'State'}
            options={country === Country.Canada ? CANADA_STATE_OPTIONS : USA_STATE_OPTIONS}
            disabled={isRegionDisabled}
          />
        </div>
      </div>
      <div className="vendor-lead-step-address-tab__container__row">
        <div className="w-100">
          <TextInputField name="address.street" label="Street" />
        </div>
        <div className="vendor-lead-step-address-tab__container__subrow">
          <div className="w-100">
            <TextInputField name="address.city" label="City" />
          </div>
          <TextInputField
            name="address.postalCode"
            type="zip"
            country={country ?? undefined}
            label={country === Country.Canada ? 'Postal Code' : 'Zip'}
            disabled={isRegionDisabled}
          />
        </div>
      </div>

      <div className="vendor-lead-step__layout__center__footer-actions">
        <VendorLeadStepLayoutNextButton />
      </div>
    </div>
  );
}
